import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, Navigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ShoppingBag, ChevronRight, Plus, Search, X, ArrowUpDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useCategories } from '../context/CategoriesContext';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { useToast } from '../hooks/use-toast';
import { resolveImageUrl } from '../components/ImageUpload';
import { NotifyWhenInStock } from '../components/NotifyWhenInStock';
import { priceWithTier } from '../lib/pricing';
import Rial from '../components/Rial';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SORT_OPTIONS = [
  { value: 'default', label: 'Featured' },
  { value: 'price_asc', label: 'Price: Low → High' },
  { value: 'price_desc', label: 'Price: High → Low' },
  { value: 'name_asc', label: 'Name: A → Z' },
];

const Category = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  // `?p=<product_id>` deep-link — used by back-in-stock SMS links, share
  // links, etc. We scroll the card into view and pulse a highlight ring.
  const deepLinkProductId = searchParams.get('p');
  const { categories, loading: catLoading } = useCategories();
  const category = categories.find((c) => c.slug === slug);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qtyMap, setQtyMap] = useState({}); // { [productId]: string }
  const [search, setSearch] = useState('');
  const [activeTags, setActiveTags] = useState(new Set());
  const [activeSizes, setActiveSizes] = useState(new Set());
  const [activeCustom, setActiveCustom] = useState({}); // { [filterKey]: Set<value> }
  const [sort, setSort] = useState('default');
  const { add, setOpen } = useCart();
  const { discountPct, pricingTier, priceOverrides, email: customerEmail } = useCustomerAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (!category) return;
    setLoading(true);
    setSearch('');
    setActiveTags(new Set());
    setActiveSizes(new Set());
    setActiveCustom({});
    setSort('default');
    axios
      .get(`${API}/products?category=${slug}`)
      .then(({ data }) => setProducts(data))
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, [slug, category]);

  // Scroll deep-linked product into view once the cards are rendered. The
  // 250ms delay lets React commit the list and the lazy-loaded images
  // claim their height before we measure scroll position.
  useEffect(() => {
    if (!deepLinkProductId || loading) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`product-${deepLinkProductId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 250);
    return () => clearTimeout(t);
  }, [deepLinkProductId, loading, products.length]);

  // Compute available tag + size facets — admin-defined master list takes precedence
  const allTags = useMemo(() => {
    if (category?.tag_options?.length) return [...category.tag_options];
    return Array.from(new Set(products.map((p) => p.tag).filter(Boolean))).sort();
  }, [products, category]);
  const allSizes = useMemo(() => {
    const fromAdmin = category?.size_options?.length ? [...category.size_options] : null;
    const src = fromAdmin || Array.from(new Set(products.map((p) => p.size).filter(Boolean)));
    return src.sort((a, b) => {
      const na = parseFloat(a);
      const nb = parseFloat(b);
      if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
      return a.localeCompare(b);
    });
  }, [products, category]);

  const customFilters = category?.custom_filters || [];

  // Apply filters
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = products.filter((p) => {
      if (q && !p.name.toLowerCase().includes(q)) return false;
      if (activeTags.size > 0 && !activeTags.has(p.tag)) return false;
      if (activeSizes.size > 0 && !activeSizes.has(p.size)) return false;
      for (const f of customFilters) {
        const set = activeCustom[f.key];
        if (set && set.size > 0) {
          const val = (p.custom_attrs || {})[f.key];
          if (!val || !set.has(val)) return false;
        }
      }
      return true;
    });
    switch (sort) {
      case 'price_asc':
        list = [...list].sort((a, b) => a.price - b.price);
        break;
      case 'price_desc':
        list = [...list].sort((a, b) => b.price - a.price);
        break;
      case 'name_asc':
        list = [...list].sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        break;
    }
    // Bubble "Coming Soon" items to the top across every sort order so the
    // newest products are always front-and-centre. "Out of stock" items
    // sink to the bottom — both are stable within their tier so the
    // user-chosen sort still decides ordering inside each group.
    const rank = (p) => {
      const s = p.stock_status || (p.in_stock === false ? 'out_of_stock' : 'in_stock');
      if (s === 'coming_soon') return 0;
      if (s === 'in_stock') return 1;
      return 2; // out_of_stock
    };
    list = [...list].sort((a, b) => rank(a) - rank(b));
    return list;
  }, [products, search, activeTags, activeSizes, activeCustom, sort, customFilters]);

  const hasActiveFilter =
    search ||
    activeTags.size > 0 ||
    activeSizes.size > 0 ||
    sort !== 'default' ||
    Object.values(activeCustom).some((s) => s && s.size > 0);

  const toggleSet = (set, setter, value) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setter(next);
  };

  const toggleCustom = (key, value) => {
    setActiveCustom((prev) => {
      const next = { ...prev };
      const set = new Set(next[key] || []);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      next[key] = set;
      return next;
    });
  };

  const clearFilters = () => {
    setSearch('');
    setActiveTags(new Set());
    setActiveSizes(new Set());
    setActiveCustom({});
    setSort('default');
  };

  if (catLoading) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading…</div>;
  if (!category) return <Navigate to="/" replace />;

  const handleAdd = (p) => {
    const status = p.stock_status || (p.in_stock === false ? 'out_of_stock' : 'in_stock');
    if (status === 'coming_soon') {
      toast({ title: 'Coming soon', description: `${p.name} isn’t available yet — we’ll launch it shortly.` });
      return;
    }
    if (status === 'out_of_stock' || p.in_stock === false) {
      toast({ title: 'Out of stock', description: `${p.name} is currently unavailable.` });
      return;
    }
    const raw = qtyMap[p.id];
    const qty = parseInt(raw, 10);
    const minQty = p.min_qty ?? 1;
    const uom = p.uom || 'ctn';
    if (!Number.isFinite(qty) || qty < 1) {
      toast({ title: 'Enter a quantity', description: `Type how many ${uom} to add.` });
      return;
    }
    if (qty < minQty) {
      toast({ title: 'Minimum order quantity', description: `${p.name} requires at least ${minQty} ${uom}.` });
      return;
    }
    add(
      {
        id: p.id,
        name: p.name,
        price: priceWithTier(p.price, discountPct, priceOverrides?.[p.id]),
        retail_price: p.price,
        size: p.size,
        size_type: p.size_type || 'size',
        image: resolveImageUrl(p.image) || resolveImageUrl(category.image),
        bg: category.bg,
        uom,
        min_qty: minQty,
        vat_applicable: !!p.vat_applicable,
      },
      qty,
    );
    setQtyMap((m) => ({ ...m, [p.id]: '' }));
    toast({
      title: 'Added to cart',
      description: `${qty} ${uom} × ${p.name} — OMR ${(priceWithTier(p.price, discountPct, priceOverrides?.[p.id]) * qty).toFixed(3)}`,
    });
  };

  return (
    <main className="bg-[#F8FAFC] min-h-screen">
      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: `linear-gradient(160deg, ${category.bg} 0%, #ffffff 70%)` }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-6">
            <Link to="/" className="hover:text-slate-900">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-900 font-medium">{category.name}</span>
          </div>
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div
                className="inline-block text-[11px] font-bold tracking-widest uppercase px-3 py-1.5 rounded-full text-white mb-5"
                style={{ background: category.color }}
              >
                {category.tagline}
              </div>
              <h1 className="layout-page-title font-display text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05]">
                {category.name}
              </h1>
              <p className="layout-page-subtitle mt-5 text-base sm:text-lg text-slate-600 max-w-lg">{category.desc}</p>
              <div className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-700">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  In stock
                </div>
                <span className="text-slate-300">·</span>
                <div>Free delivery in Muscat</div>
                <span className="text-slate-300">·</span>
                <div>{products.length} SKUs</div>
                <span className="text-slate-300">·</span>
                <div>Trade pricing available</div>
              </div>
            </div>
            <div className="flex items-center justify-center relative rounded-2xl overflow-hidden">
              {category.hero_banner_image ? (
                <img
                  src={resolveImageUrl(category.hero_banner_image)}
                  alt={category.name}
                  className="w-full h-full max-h-80 object-cover rounded-2xl shadow-xl"
                />
              ) : (
                <img
                  src={resolveImageUrl(category.image)}
                  alt={category.name}
                  className="max-h-80 w-auto object-contain drop-shadow-2xl floaty"
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Products grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {discountPct > 0 && (
          <div
            className="mb-6 flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border border-emerald-200 bg-emerald-50"
            data-testid="trade-tier-banner"
          >
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-500 text-white font-bold text-xs">
                {discountPct}%
              </span>
              <div className="text-sm text-emerald-900">
                <span className="font-bold uppercase tracking-wider text-[10px] text-emerald-700 mr-1.5">
                  {pricingTier} tier
                </span>
                Your B2B trade discount is applied to every product below.
              </div>
            </div>
          </div>
        )}
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="text-[11px] font-bold tracking-[0.2em] text-[#0078D4] uppercase mb-2">Catalogue</div>
            <h2 className="layout-section-title font-display text-2xl sm:text-3xl text-slate-900">All products</h2>
          </div>
          <Link to="/" className="text-sm text-slate-600 hover:text-[#0078D4] flex items-center gap-1 underline-hover">
            <ArrowLeft className="w-4 h-4" /> Back to catalogue
          </Link>
        </div>

        {/* Search + Sort */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${products.length} products by name…`}
              className="h-11 pl-9 pr-9 bg-white"
              data-testid="category-search"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {category.show_sort_filter !== false && (
            <div className="relative">
              <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="h-11 pl-9 pr-9 rounded-md border border-neutral-300 bg-white text-sm font-medium appearance-none w-full sm:w-56 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#0078D4] focus:border-transparent"
                data-testid="category-sort"
                aria-label={category.sort_filter_label || 'Sort by'}
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Tag + Size + custom filter chips */}
        {((category.show_tag_filter !== false && allTags.length > 0) ||
          (category.show_size_filter !== false && allSizes.length > 0) ||
          customFilters.length > 0) && (
          <div className="flex flex-col gap-2 mb-5">
            {category.show_tag_filter !== false && allTags.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mr-1">
                  {category.tag_filter_label || 'Tags'}:
                </span>
                {allTags.map((t) => {
                  const active = activeTags.has(t);
                  return (
                    <button
                      key={t}
                      onClick={() => toggleSet(activeTags, setActiveTags, t)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                        active
                          ? 'bg-[#0F172A] text-white border-[#0F172A]'
                          : 'bg-white text-neutral-700 border-neutral-300 hover:border-[#0078D4]'
                      }`}
                      data-testid={`tag-filter-${t.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            )}
            {category.show_size_filter !== false && allSizes.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mr-1">
                  {category.size_filter_label || 'Sizes'}:
                </span>
                {allSizes.map((s) => {
                  const active = activeSizes.has(s);
                  return (
                    <button
                      key={s}
                      onClick={() => toggleSet(activeSizes, setActiveSizes, s)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                        active
                          ? 'bg-[#0078D4] text-white border-[#0078D4]'
                          : 'bg-white text-neutral-700 border-neutral-300 hover:border-[#0078D4]'
                      }`}
                      data-testid={`size-filter-${s.replace(/\s+/g, '-')}`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            )}
            {customFilters.map((f) => (
              <div key={f.id || f.key} className="flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mr-1">
                  {f.label}:
                </span>
                {(f.options || []).map((o) => {
                  const set = activeCustom[f.key] || new Set();
                  const active = set.has(o.value || o.label);
                  const val = o.value || o.label;
                  return (
                    <button
                      key={o.id || val}
                      onClick={() => toggleCustom(f.key, val)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                        active
                          ? 'bg-[#7C3AED] text-white border-[#7C3AED]'
                          : 'bg-white text-neutral-700 border-neutral-300 hover:border-[#7C3AED]'
                      }`}
                      data-testid={`custom-filter-${f.key}-${val.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {o.label || val}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {/* Result count + Clear */}
        <div className="flex items-center justify-between mb-5 text-sm">
          <div className="text-neutral-600">
            Showing <span className="font-semibold text-[#0F172A]">{filtered.length}</span> of {products.length} products
          </div>
          {hasActiveFilter && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1 text-[#0078D4] hover:underline font-medium"
              data-testid="clear-filters"
            >
              <X className="w-3.5 h-3.5" /> Clear filters
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center text-neutral-500 py-16">Loading products…</div>
        ) : products.length === 0 ? (
          <div className="text-center text-neutral-500 py-16">No products in this division yet.</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <div className="inline-flex w-14 h-14 rounded-full bg-neutral-100 items-center justify-center mb-3">
              <Search className="w-5 h-5 text-neutral-400" />
            </div>
            <h3 className="text-base font-semibold text-[#0F172A]">No products match</h3>
            <p className="text-sm text-neutral-500 mt-1">Try clearing filters or searching for something different.</p>
            <button
              onClick={clearFilters}
              className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#0078D4] hover:underline"
            >
              <X className="w-3.5 h-3.5" /> Clear filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {filtered.map((p) => {
              const uom = p.uom || 'ctn';
              const minQty = p.min_qty ?? 1;
              const status = p.stock_status || (p.in_stock === false ? 'out_of_stock' : 'in_stock');
              const comingSoon = status === 'coming_soon';
              const outOfStock = status === 'out_of_stock' || (p.in_stock === false && !comingSoon);
              const unavailable = comingSoon || outOfStock;
              return (
                <div
                  key={p.id}
                  id={`product-${p.id}`}
                  className={`bg-white rounded-2xl overflow-hidden border border-neutral-200 card-lift group flex flex-col ${unavailable ? 'relative' : ''} ${deepLinkProductId === p.id ? 'ring-2 ring-[#0078D4] ring-offset-2 animate-pulse-once' : ''}`}
                  data-testid={comingSoon ? `coming-soon-card-${p.id}` : outOfStock ? `oos-card-${p.id}` : `product-card-${p.id}`}
                >
                  <div
                    className="relative aspect-square flex items-center justify-center p-6"
                    style={{ background: category.bg }}
                  >
                    {p.tag && !unavailable && (
                      <Badge
                        className="absolute top-3 left-3 text-[10px] font-bold tracking-wide uppercase border-0 text-white"
                        style={{ background: p.tag_color || category.color }}
                      >
                        {p.tag}
                      </Badge>
                    )}
                    {comingSoon && (
                      <div className="absolute top-3 left-3 px-2 py-1 rounded-md text-[10px] font-bold tracking-wider uppercase bg-amber-500 text-white shadow-sm" data-testid={`coming-soon-badge-${p.id}`}>
                        Coming Soon
                      </div>
                    )}
                    {outOfStock && (
                      <div className="absolute top-3 left-3 px-2 py-1 rounded-md text-[10px] font-bold tracking-wider uppercase bg-red-600 text-white shadow-sm" data-testid={`oos-badge-${p.id}`}>
                        Out of stock
                      </div>
                    )}
                    <img
                      src={resolveImageUrl(p.image) || resolveImageUrl(category.image)}
                      alt={p.name}
                      className={`max-h-full max-w-full object-contain transition-transform duration-500 ${unavailable ? '' : 'group-hover:scale-110'}`}
                    />
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <div className="text-[15px] font-semibold text-[#0F172A] leading-tight line-clamp-2 min-h-[2.5rem] product-card-name">
                      {p.name}
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      {p.size && <div className="text-xs text-neutral-500 product-card-size">{p.size}</div>}
                      <div className="text-[10px] font-semibold tracking-wider uppercase text-neutral-400 product-card-minqty">
                        Min {minQty} {uom}
                      </div>
                    </div>
                    <div className="mt-3 font-bold text-[#0F172A] product-card-price">
                      {(() => {
                        const override = priceOverrides?.[p.id];
                        const hasOverride = override !== undefined && override !== null && Number(override) >= 0;
                        const effective = priceWithTier(p.price, discountPct, override);
                        const showStrike = hasOverride ? Number(override) < Number(p.price) : discountPct > 0;
                        if (showStrike) {
                          return (
                            <>
                              <span className="text-xs text-neutral-400 line-through font-medium mr-1.5 product-card-price-strike">
                                <Rial /> {p.price.toFixed(3)}
                              </span>
                              <span className="text-emerald-600"><Rial /> {effective.toFixed(3)}</span>
                              <span className="text-xs font-medium text-neutral-500 product-card-uom"> / {uom}</span>
                              {hasOverride ? (
                                <span
                                  className="ml-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full align-middle"
                                  data-testid={`price-override-badge-${p.id}`}
                                  title="Your contracted price"
                                >
                                  Your price
                                </span>
                              ) : (
                                <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full align-middle">
                                  -{discountPct}%
                                </span>
                              )}
                            </>
                          );
                        }
                        return (
                          <><Rial /> {effective.toFixed(3)} <span className="text-xs font-medium text-neutral-500 product-card-uom">/ {uom}</span></>
                        );
                      })()}
                    </div>
                    {outOfStock ? (
                      <NotifyWhenInStock
                        productId={p.id}
                        defaultEmail={customerEmail || ''}
                        testId={`oos-notify-${p.id}`}
                      />
                    ) : comingSoon ? (
                      <div className="mt-3 px-3 py-2 rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-xs font-semibold text-center" data-testid={`coming-soon-msg-${p.id}`}>
                        Coming soon — stay tuned
                      </div>
                    ) : (
                      <div className="mt-3 flex items-stretch gap-2">
                        <div className="flex-1 inline-flex items-center gap-1 bg-white border border-neutral-300 rounded-md px-2 h-10 focus-within:ring-2 focus-within:ring-[#0078D4] focus-within:border-transparent">
                          <Input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            autoComplete="off"
                            placeholder="Qty"
                            value={qtyMap[p.id] || ''}
                            onChange={(e) =>
                              setQtyMap((m) => ({ ...m, [p.id]: e.target.value.replace(/[^\d]/g, '') }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAdd(p);
                              }
                            }}
                            className="h-8 min-w-0 w-full px-0 border-0 shadow-none focus-visible:ring-0 text-base font-bold text-[#0F172A] text-left bg-transparent"
                            data-testid={`qty-input-${p.id}`}
                          />
                          <span className="text-[10px] font-bold text-neutral-500 uppercase tracking-wide whitespace-nowrap">
                            {uom}
                          </span>
                        </div>
                        <Button
                          onClick={() => handleAdd(p)}
                          size="sm"
                          className="h-10 px-3 rounded-md text-white font-semibold whitespace-nowrap"
                          style={{ background: category.color }}
                          data-testid={`add-to-cart-${p.id}`}
                        >
                          <Plus className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Add</span>
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-12 text-center">
          <Button
            onClick={() => setOpen(true)}
            variant="outline"
            className="rounded-full px-6 h-11 border-[#0078D4] text-[#0078D4] hover:bg-[#DEECF9]"
          >
            <ShoppingBag className="w-4 h-4 mr-2" /> View cart
          </Button>
        </div>
      </section>
    </main>
  );
};

export default Category;
